import { Pencil, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import { deleteUserByID, getUserByID } from '../../../services/userService';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import Loader from '../../../components/Loader/Loader';
import Pagination from '../../../components/Pagination/Pagination';

interface Department {
  id: number;
  name: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  user_code: string | null;
  role: string | null;
  shift: string | null;
  department_id: number | null;
  department: Department | null;
  profile_photo: string | null;
  user_type?: {
    title: string;
  } | null;
}

interface UsersTableProps {
  users: User[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onEdit: (user: User) => void;
  onError?: (message: string) => void;
  // PAGINATION PROPS
  currentPage: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  loading,
  error,
  onRefresh,
  onEdit,
  onError,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);

  // Delete flow
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Success message text
  const [successMessage, setSuccessMessage] = useState<string>('Operation successful.');

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center p-6">
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm text-left border rounded shadow">
        <thead className="text-gray-600 text-xs">
          <tr className="text-left text-sm font-semibold text-gray-700 h-[45px]">
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Sl. No</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">User ID</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">User Name</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Email</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Role</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Shift</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Department</th>
            <th className="px-4 py-1 border text-xs font-medium text-gray-500">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.length > 0 ? (
            users.map((user, index) => (
              <tr
                key={user.id}
                className="cursor-pointer text-sm text-gray-800 transition-colors hover:bg-gray-50 border-b"
                onClick={() => navigate(`/Users/${user.id}`)}
                onKeyDown={(event) => {
                  if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    navigate(`/Users/${user.id}`);
                  }
                }}
                tabIndex={0}
                aria-label={`View details for ${user.name}`}
              >
                <td className="px-4 py-3 text-gray-500 border text-center">{(currentPage - 1) * pageSize + index + 1}</td>
                <td className="px-4 py-3 border font-medium">{user.id}</td>
                <td className="px-4 py-3  flex items-center gap-3 font-medium">
                  <img
                    src={
                      user.profile_photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=0D8ABC&color=fff`
                    }
                    alt={user.name}
                    className="w-8 h-8 rounded-full"
                  />
                  <span>{user.name}</span>
                </td>
                <td className="px-4 py-3 border font-medium">{user.email || '—'}</td>
                <td className="px-4 py-3 border font-medium">{user.user_type?.title || user.role || '—'}</td>
                <td className="px-4 py-3 border font-medium">{user.shift || '—'}</td>
                <td className="px-4 py-3 border font-medium">{user.department?.name || '—'}</td>
                <td className="p-3 flex gap-2">
                  <>
                    <button
                      className="text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteId(user.id);
                        setIsDeleteOpen(true);
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                    <button
                      className="text-blue-500"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const editUser = await getUserByID(user.id);
                          onEdit(editUser);
                        } catch (err: any) {
                          console.error('Error fetching user:', err);
                          const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load data for editing';
                          onError?.(errorMessage);
                        }
                      }}
                    >
                      <Pencil size={16} />
                    </button>
                  </>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={8} className="text-center py-6 text-gray-500 text-sm">
                No users found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
        onButtonClick={() => setShowSuccess(false)}
      />

      <ConfirmModal
        isOpen={isDeleteOpen}
        title="Are you sure you want to delete this User?"
        message=""
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onCancel={() => {
          setIsDeleteOpen(false);
          setDeleteId(null);
        }}
        onConfirm={async () => {
          if (deleteId !== null) {
            try {
              await deleteUserByID(deleteId);
              setSuccessMessage('User deleted successfully.');
              setShowSuccess(true);
              onRefresh();
            } catch (err: any) {
              console.error('Error deleting user:', err);
              const errorMessage = err?.response?.data?.message || err?.message || 'Failed to delete user';
              onError?.(errorMessage);
            }
          }
          setIsDeleteOpen(false);
          setDeleteId(null);
        }}
      />
    </div>
  );
};

export default UsersTable;
